import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PageRootLanding } from '../../../ui/components/PageBuilder/PageRootLanding/PageRootLanding';
import { Button } from '../../../ui/components/Button/Button';
import { Heading } from '../../../ui/components/Heading/Heading';

import styles from './ServicePage.module.scss';
import { JessicaArticlesCards } from '../OtherServices/JessicaArticlesCards';
import { ServiceCard } from './ServiceCard/ServiceCard';
import { AppointmentModal } from '../../components/AppointmentModal/AppointmentModal';

const articleTitles: Record<string, string> = {
  'Vitamin Infusion': 'Vitamin infusion and nutrient support',
  'Hormone Replacement': 'Hormonal balance through different life stages',
  'Functional Medicine': 'Functional medicine and root-cause thinking',
  'Platelet Rich Plasma': 'What to know about platelet-rich plasma',
  'Stem Cells': 'Cellular repair and stem cell concepts',
  'Pain Control Management': 'Pain, mobility, and daily comfort',
};

const articleSummaries: Record<string, string> = {
  'Vitamin Infusion':
    'A practical note about hydration, nutrient support, and questions to bring into a private consultation.',
  'Hormone Replacement':
    'A readable overview of how hormonal shifts can shape energy, sleep, mood, and long-term wellness.',
  'Functional Medicine':
    'An educational look at symptom patterns, lifestyle context, and root-cause conversations.',
  'Platelet Rich Plasma':
    'A simple explanation of platelets, tissue repair language, and individualized care planning.',
  'Stem Cells':
    'A concise article about cellular health, recovery concepts, and medical assessment.',
  'Pain Control Management':
    'A wellness note about pain patterns, function, mobility, and personalized next steps.',
};

const pageHighlights = [
  {
    value: '6',
    label: 'Educational notes',
  },
  {
    value: '15+',
    label: 'Years of experience',
  },
  {
    value: '1:1',
    label: 'Private consultation',
  },
];

export const ServicePage = () => {
  const navigate = useNavigate();
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);

  const renderArticles = () => {
    return JessicaArticlesCards.map((service, index) => (
      <ServiceCard
        key={service.title}
        {...service}
        title={articleTitles[service.title] || service.title}
        classname={index === 0 ? styles.newest : undefined}
        isNew={index === 0}
        serviceNumber={`${index + 1}`.padStart(2, '0')}
        summary={articleSummaries[service.title]}
      />
    ));
  };

  const renderHighlights = () => {
    return pageHighlights.map((highlight) => (
      <li key={highlight.label}>
        <strong>{highlight.value}</strong>
        <span>{highlight.label}</span>
      </li>
    ));
  };

  return (
    <PageRootLanding
      colorPalettes="doctor-1"
      title="Wellness Notes"
      configuration={{ showTitle: false }}
      navbarItems={[
        {
          endpoint: '/',
          icon: 'home1',
          name: 'Go home',
          onClick: () => {
            navigate('/');
          },
        },
      ]}
    >
      <>
        <AppointmentModal
          isOpen={isAppointmentOpen}
          onClose={() => setIsAppointmentOpen(false)}
        />
        <main className={styles.root}>
          <section className={styles.hero}>
            <div className={styles['hero-copy']}>
              <span className={styles.eyebrow}>Functional medicine care</span>
              <Heading className={styles.title} headingLevel="h1">
                Educational notes for a clearer health conversation
              </Heading>
              <p className={styles.subtitle}>
                Read approachable articles about preventive, functional, and
                regenerative care, then use any topic as a starting point for a
                private appointment.
              </p>
              <div className={styles.actions}>
                <Button
                  btnStyle="secondary"
                  className={styles.cta}
                  glassEffect
                  text="Book an appointment"
                  onClick={() => setIsAppointmentOpen(true)}
                />
                <Button
                  btnStyle="primary"
                  className={styles.cta}
                  glassEffect
                  text="Browse notes"
                  href="#wellness-notes"
                />
              </div>
            </div>
            <div className={styles['hero-panel']} aria-label="Article preview">
              <img
                className={styles['hero-image']}
                src={JessicaArticlesCards[0].img}
                alt="Vitamin infusion article"
              />
              <div className={styles['panel-card']}>
                <span>Featured note</span>
                <strong>{articleTitles[JessicaArticlesCards[0].title]}</strong>
              </div>
            </div>
          </section>
          <ul className={styles.highlights}>{renderHighlights()}</ul>
          <section id="wellness-notes" className={styles.services}>
            <div className={styles['section-header']}>
              <span className={styles.eyebrow}>Article index</span>
              <Heading className={styles['section-title']} headingLevel="h2">
                Read by topic
              </Heading>
            </div>
            <ul className={styles.grid}>{renderArticles()}</ul>
          </section>
        </main>
      </>
    </PageRootLanding>
  );
};
